// Lost and found's places (PMU-236): in the staff app a bar under Now and Bus that opens its page;
// in the parent app a card at the top of Messages and its page; in the console a page.
import { useState } from "react";
import { navigate, usePath, type UiPart } from "@vavr/screens/shared";
import { G, useGuardian } from "@vavr/screens/guardian";
import { T, useTeacher } from "@vavr/screens/teacher";
import { useConsole } from "@vavr/screens/console";
import { Button, EmptyState } from "@vavr/ui";
import { ItemView, ParentCard, ParentLostFound, StaffBar, StaffLostFound } from "./screens.tsx";
import { safetyNav } from "../safety/page.tsx";

const itemId = (path: string, base: string) => path.startsWith(`${base}/lost-found/`) ? decodeURIComponent(path.slice(`${base}/lost-found/`.length).split("/")[0]!) : null;

function StaffPage() {
  const { site } = useTeacher();
  const id = itemId(usePath(), T);
  return id ? <ItemView id={id} tz={site.tz} /> : <StaffLostFound siteId={site.id} tz={site.tz} open={(i) => navigate(`${T}/lost-found/${i}`)} />;
}

function ParentPage({ id }: { id?: string }) {
  const { overview } = useGuardian();
  const tz = overview.sites[0]?.site.tz;
  return id ? <ItemView parent id={id} tz={tz} /> : <ParentLostFound tz={tz} open={(i) => navigate(`${G}/lost-found/${i}`)} />;
}

/** The console: the list beside the open item, filling the page under the top bar (like Messages). */
function ConsolePage() {
  const { site } = useConsole();
  const [open, setOpen] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  return (
    <div className={`plf-console plf-console--page${open ? " plf-console--selected" : ""}`}>
      <div className="plf-console__list"><StaffLostFound compact siteId={site.id} tz={site.tz} open={setOpen} selectedId={open} version={version} /></div>
      <section className="plf-console__open" aria-label="Item">
        {open ? (
          <div className="plf-console__card">
            <div className="plf-console__back"><Button size="sm" variant="ghost" icon="chevron-left" onClick={() => setOpen(null)}>Back to the list</Button></div>
            <ItemView key={open} id={open} tz={site.tz} onChanged={() => setVersion((x) => x + 1)} />
          </div>
        ) : <EmptyState icon="search" title="Choose an item" description="Pick something from the list to see its photos, name the owner, hand it back or see its history." />}
      </section>
    </div>
  );
}

function Bar() {
  const { site } = useTeacher();
  return <StaffBar siteId={site.id} open={() => navigate(`${T}/lost-found`)} />;
}

export const lostfoundUi: UiPart = {
  id: "lostfound",
  slots: {
    "staff.page": [{ id: "lostfound", path: "lost-found", title: "Lost and found", permission: "lostfound:use", render: () => <StaffPage /> }],
    "staff.footer": [{ id: "lostfound", tabs: ["now", "bus"], render: () => <Bar /> }],
    // At the top of Messages (owner's choice, 2026-09-30); its pages sit under the Messages tab.
    "guardian.inbox": [{ id: "lostfound", render: () => <ParentCard open={() => navigate(`${G}/lost-found`)} /> }],
    "guardian.page": [
      { id: "lostfound", path: "lost-found", title: "Lost and found", tab: "messages", render: () => <ParentPage /> },
      { id: "lostfound-item", path: "lost-found/:id", title: "Lost and found", tab: "messages", render: (p) => <ParentPage id={p.id} /> },
    ],
    "console.nav": [{ id: "lostfound", label: "Lost and found", icon: "search", path: "lost-found", title: "Lost and found", permission: "lostfound:use", fullBleed: true, render: () => <ConsolePage /> }, safetyNav],
  },
};
